import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Eye } from 'lucide-react';
import {
  SAP_PUSH_STEP_LABELS,
  type SapPushRunSummary,
  type SapPushStepSummary,
  type SapStepStatus,
} from '@hcc/types';
import { Pill } from '../../../../components/ui/Pill';
import { ModalShell } from '../../../../components/ui/ModalShell';
import { api } from '../../../../lib/api';

const STEP_TONE: Record<SapStepStatus, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  pending: 'neutral',
  running: 'info',
  succeeded: 'success',
  warning: 'warning',
  failed: 'danger',
  skipped: 'neutral',
};

const RUN_TONE: Record<SapPushRunSummary['status'], 'success' | 'danger' | 'info' | 'neutral'> = {
  running: 'info',
  succeeded: 'success',
  failed: 'danger',
  dry_run_complete: 'neutral',
};

/**
 * Run history with expandable per-step diagnostics. Polls while a run
 * is live (a push takes ~15-30s). The payload viewer hits the audited
 * decrypt route, so opening it is itself an access-logged action.
 */
export function SapRunLog({ personId }: { personId: string }) {
  const runs = useQuery({
    queryKey: ['sap-runs', personId],
    queryFn: () => api<{ runs: SapPushRunSummary[] }>(`/api/admin/sap/people/${personId}/runs`),
    refetchInterval: (q) =>
      q.state.data?.runs.some((r) => r.status === 'running') ? 1500 : false,
  });

  if (!runs.data || runs.data.runs.length === 0) {
    return (
      <section className="rounded-lg border border-line/10 bg-surface px-5 py-4 shadow-sm">
        <h3 className="text-sm font-semibold">Push history</h3>
        <p className="mt-1 text-sm text-fg-muted">No runs yet.</p>
      </section>
    );
  }
  return (
    <section className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
      <div className="border-b border-line/10 px-5 py-3">
        <h3 className="text-sm font-semibold">Push history</h3>
      </div>
      <div className="divide-y divide-divider/10">
        {runs.data.runs.map((run) => (
          <RunRow key={run.id} run={run} />
        ))}
      </div>
    </section>
  );
}

function RunRow({ run }: { run: SapPushRunSummary }) {
  const [open, setOpen] = useState(run.status === 'running');
  const detail = useQuery({
    queryKey: ['sap-run', run.id],
    queryFn: () =>
      api<{ run: SapPushRunSummary; steps: SapPushStepSummary[] }>(`/api/admin/sap/runs/${run.id}`),
    enabled: open,
    refetchInterval: run.status === 'running' ? 1500 : false,
  });

  return (
    <div className="px-5 py-3">
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left text-sm"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-fg-dim" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-fg-dim" />
        )}
        <span className="font-medium">{run.dry_run ? 'Dry run' : 'Push'}</span>
        <Pill tone={RUN_TONE[run.status]}>{run.status.replace(/_/g, ' ')}</Pill>
        {run.sap_employee_id ? (
          <span className="text-fg-muted">employee {run.sap_employee_id}</span>
        ) : null}
        <span className="ml-auto text-xs text-fg-dim">
          {new Date(run.created_at).toLocaleString()}
        </span>
      </button>
      {run.error ? <p className="ml-6 mt-1 text-xs text-danger-soft-fg">{run.error}</p> : null}
      {open && detail.data ? (
        <div className="ml-6 mt-2 space-y-1">
          {detail.data.steps.map((s) => (
            <StepRow key={s.id} runId={run.id} step={s} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function StepRow({ runId, step }: { runId: string; step: SapPushStepSummary }) {
  const [payloadOpen, setPayloadOpen] = useState(false);
  return (
    <div className="flex flex-wrap items-baseline gap-2 text-sm">
      <span className="w-56 shrink-0 text-fg-muted">{SAP_PUSH_STEP_LABELS[step.step_key]}</span>
      <Pill tone={STEP_TONE[step.status]}>{step.status}</Pill>
      {step.sap_severity !== null && step.sap_severity > 0 ? (
        <span className="text-xs text-fg-dim">severity {step.sap_severity}</span>
      ) : null}
      {step.duration_ms !== null ? (
        <span className="text-xs text-fg-dim tabular-nums">{step.duration_ms}ms</span>
      ) : null}
      {step.change_state_before ? (
        <span className="text-xs text-fg-dim" title="ChangeStateID before → after">
          {step.change_state_before} → {step.change_state_after ?? '?'}
        </span>
      ) : null}
      {step.error ? <span className="text-xs text-danger-soft-fg">{step.error}</span> : null}
      {'has_payload' in step && (step as { has_payload?: boolean }).has_payload ? (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
          onClick={() => setPayloadOpen(true)}
        >
          <Eye className="h-3.5 w-3.5" /> payload
        </button>
      ) : null}
      {payloadOpen ? (
        <PayloadModal runId={runId} step={step} onClose={() => setPayloadOpen(false)} />
      ) : null}
    </div>
  );
}

function PayloadModal({
  runId,
  step,
  onClose,
}: {
  runId: string;
  step: SapPushStepSummary;
  onClose: () => void;
}) {
  const payload = useQuery({
    queryKey: ['sap-payload', runId, step.id],
    queryFn: () =>
      api<{ request: string | null; response: string | null }>(
        `/api/admin/sap/runs/${runId}/steps/${step.id}/payload`,
      ),
  });
  return (
    <ModalShell open onClose={onClose} titleId="sap-payload-title" maxWidth="max-w-3xl">
      <div className="p-5">
        <h2 id="sap-payload-title" className="text-base font-semibold">
          {SAP_PUSH_STEP_LABELS[step.step_key]} payload
        </h2>
        <p className="mt-1 text-xs text-fg-muted">
          Exact bytes exchanged with SAP. Viewing this is recorded in the access log.
        </p>
        {payload.isLoading ? <p className="mt-3 text-sm text-fg-muted">Decrypting…</p> : null}
        {payload.data?.request ? (
          <PayloadBlock label="Request" body={payload.data.request} />
        ) : null}
        {payload.data?.response ? (
          <PayloadBlock label="Response" body={payload.data.response} />
        ) : null}
      </div>
    </ModalShell>
  );
}

function PayloadBlock({ label, body }: { label: string; body: string }) {
  return (
    <div className="mt-3">
      <div className="text-xs font-medium uppercase tracking-wider text-fg-dim">{label}</div>
      <pre className="mt-1 max-h-64 overflow-auto rounded-token-sm bg-surface-inset p-3 text-xs leading-relaxed">
        {body}
      </pre>
    </div>
  );
}
